import React, { forwardRef, ReactNode, useCallback } from 'react'
import { useFormContext, useController } from 'react-hook-form'
import { Checkbox } from 'modules/ui/atoms/Checkbox'
import { InputCaption } from 'modules/ui/atoms/Input/InputContainer'
import Label from 'modules/ui/atoms/Typography/Label'

export type CheckboxFieldProps = {
  id: string
  centered?: boolean
  children: ReactNode
  error?: { message: string }
  label?: string
  required?: boolean
  dataId?: string
  autoFocus?: boolean
  defaultValue?: boolean
  onFocus?: () => void
}

const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  (
    {
      id,
      label,
      error,
      children,
      required,
      centered,
      dataId = '',
      autoFocus,
      defaultValue,
      onFocus,
    },
    ref,
  ) => {
    const { control } = useFormContext()

    const { field } = useController({
      name: id,
      control,
      defaultValue: defaultValue ?? false,
    })

    const toggleChecked = useCallback(() => {
      field.onChange(!field.value)
    }, [field])

    return (
      <div>
        {label && (
          <Label size="sm" errored={!!error}>
            {label}
          </Label>
        )}

        <Checkbox
          required={required}
          checked={field.value}
          onBlur={field.onBlur}
          onChange={toggleChecked}
          centered={centered}
          dataId={dataId}
          ref={ref}
          autoFocus={autoFocus}
          onFocus={onFocus}
        >
          {children}
        </Checkbox>

        {error && (
          <>
            <InputCaption error={error.message} hasCounter={false} hasOverflow={false} />
          </>
        )}
      </div>
    )
  },
)

CheckboxField.displayName = 'CheckboxField'

export default CheckboxField
